import { init } from "../Lit/acc-boot/acc-boot.js";
import { formatAboPV } from "../Lit/acc-mixins/l10n.js";
import merge from "lodash-es/merge.js";
import { fetchConfig, regionalConfigPath } from "../../common/util/config";
import { store } from "../../store";
import { padAff } from "../../common/util/affPadding";

/**
 * Holds fetch() actions that request can be used by multiple instances
 */
const CONTENT_FETCH_CACHE: any = {};

/**
 * Holds global promise for init.
 * @returns {Promise}
 */
export function initCoreplusStore() {
  // if for some reason init isn't on global yet then set and return it
  if (!window.initCoreplus) {
    const selectedPeriod = store.getState().period.selectedPeriod;
    window.initCoreplus = init({
      setup: {
        globalStore: true,
        globalName: "accBoot",
        bypassLocaleInit: true,
      },
      api: {
        path: regionalConfigPath(),
      },
      bonusPeriod: selectedPeriod,
    });
  }

  return window.initCoreplus;
}

export const getValues = async () => {
  await initCoreplusStore();

  return {
    configuration: store.getState().boot.configuration,

    aff: getAff(),

    abo: getAbo(),

    user: store.getState().boot.user,
  };
};

export const normalizeLocale = (locale: string | null) => {
  if (locale) {
    // below logic is needed as there is no content for es-co. so assigning default es-es locale for content
    if (locale === "es-co") {
      locale = "es-es";
    }
    const parts = locale.split("-");
    if (parts[0] && parts[1]) {
      locale = parts[0].toLowerCase() + "-" + parts[1].toUpperCase();
    }
  }
  return locale;
};

/**
 * Returns locale from redux store
 * @returns {string}
 */
export const storedLocale = (): string => {
  return normalizeLocale(store.getState().loginLocalStorage.loginFields.locale) || "en-US";
};

/**
 * Returns marketCode from redux store
 * @returns {string}
 */
export const marketCode = () => {
  return store.getState().boot.user.isoCountryCode || "US";
};

/**
 * Returns aff from store, padded to 3 digits with leading zeros if necessary.
 * @returns {string}
 */
export const getAff = () => {
  const aff = store.getState().boot.user.aff;
  // Safety net: ensure aff is always padded even if it wasn't normalized at entry
  return padAff(aff);
};

/**
 * Returns formattedAboPV
 * @returns {string}
 */
export const formattedPV = (pv: number): string => {
  const locale = storedLocale();
  const market = marketCode();
  return formatAboPV(pv, locale, market);
};

/**
 * Returns abo from store.
 * @returns {string}
 */
export const getAbo = () => {
  return store.getState().boot.user.abo;
};

/**
 * Inits and returns aff and abo from store.
 * @returns {object}
 */
export const getAffAbo = async () => {
  await initCoreplusStore();
  return {
    aff: getAff(),
    abo: getAbo(),
  };
};

/**
 * Attempts to fetch content with the given parameters. If the fetch fails,
 * this function returns the fallback content.
 * @param {*} page - Page Id, used to build content path
 * @param {*} ssoOrigin - Single sign on origin, used to build content path
 * @param {*} locale - Locale, used to build content path
 * @param {*} fallback - Default content, returned if fetch fails
 */

export const fetchContent = async (page: string, ssoOrigin: string, locale: string, fallback = {}) => {
  // Build content path
  const fetchedConfig = await fetchConfig();
  let contentBase;
  if (fetchedConfig) contentBase = fetchedConfig.contentUrlBase;
  const contentPath = `${contentBase}/${ssoOrigin}/text/${page}/${locale}.json`;

  let responseFound;

  // Find if we have a request in the cache that we could use
  if (contentPath in CONTENT_FETCH_CACHE) {
    responseFound = CONTENT_FETCH_CACHE[contentPath];
  } else {
    // Fetch a new request
    responseFound = {
      request: fetch(contentPath),
    };

    // Setting new request into the cache
    CONTENT_FETCH_CACHE[contentPath] = responseFound;
  }

  try {
    const response = await responseFound.request;

    const data = await response.clone().json();

    return merge(fallback, data);
  } catch (e) {
    return fallback;
  }
};

/**
 * This function global user id
 * @param {String} aff - affiliate number.
 * @param {String} abo - abo number.
 * @returns {Number} global id.
 */
export const _createGlobalUserID = (aff: string, abo: string): number => {
  const hundredBillion = 100000000000;
  return Number(aff) * hundredBillion + Number(abo);
};

/**
 * Adds a log entry to a session variable for
 * the purpose of debugging user issues.
 */
export const sessionLog = (entry: string) => {
  const sessionKey = "_log";
  const previousLog = sessionStorage.getItem(sessionKey) || "";
  const date = new Date().toUTCString();
  const page = window.location.pathname;
  sessionStorage.setItem(sessionKey, `${previousLog} [${date} ${page}] ${entry}`);
};

/**
 * stringToKebabTransform
 *
 * Converts a string to kebab case
 * @param {string} input
 * @returns string
 */
export const stringToKebabTransform = (input: any) => {
  // regex performs some positive lookaround to look for locations to add a '-',  one of the following conditions exist
  // 1. add a '-' if previous character is lower case, current is upper
  // 2. add a '-' if previous is upper, current is upper, next is lower (JSONParser -> json-parser)
  // ** has some limitations (e.g. JSONAPI -> jsonapi, not json-api)
  const kebabStr =
    (input &&
      input
        .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
        .map((x: any) => x.toLowerCase())
        .join("-")) ||
    input;

  return kebabStr;
};

// Transform enum
export const AttributeTransform = {
  kebab: stringToKebabTransform,
};

Object.freeze(AttributeTransform);
